import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  LinearProgress,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import { API_ENDPOINTS } from '../config';
import uploadService from '../services/uploadService';

const NewSubmission = () => {
  const [name, setName] = useState('');
  const [taskName, setTaskName] = useState('');
  const [currentYear, setCurrentYear] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  // New state for chunked uploads
  const [pdfUploadProgress, setPdfUploadProgress] = useState(0);
  const [zipUploadProgress, setZipUploadProgress] = useState(0);
  const [pdfUploadStatus, setPdfUploadStatus] = useState('idle');
  const [zipUploadStatus, setZipUploadStatus] = useState('idle');

  const navigate = useNavigate();

  useEffect(() => {
    fetchTasks();
    fetchCurrentYear();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.TASKS);
      const data = await response.json();
      setTasks(data.tasks);
      if (data.tasks.length > 0) {
        setTaskName(data.tasks[0]);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchCurrentYear = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CURRENT_YEAR);
      const data = await response.json();
      setCurrentYear(data.year);
    } catch (error) {
      console.error('Error fetching current year:', error);
    }
  };

  const handleFileUpload = async (file, fileType, setProgress, setStatus) => {
    if (!file) return null;

    try {
      setStatus('uploading');
      const uploadId = await uploadService.startUpload(
        taskName,
        fileType,
        file,
        (progress) => {
          setProgress(progress);
        }
      );
      setStatus('completed');
      return uploadId;
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      setStatus('failed');
      setError(`Failed to upload ${fileType}: ${error.message}`);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsUploading(true);

    if (!name || !taskName || !pdfFile || !zipFile) {
      setError('Please fill in all fields and upload both files');
      setIsUploading(false);
      return;
    }

    try {
      // Upload PDF file first
      const pdfId = await handleFileUpload(
        pdfFile, 
        'pdf', 
        setPdfUploadProgress, 
        setPdfUploadStatus
      );

      if (!pdfId) {
        setIsUploading(false);
        return;
      }

      // Upload ZIP file
      const zipId = await handleFileUpload(
        zipFile, 
        'zip', 
        setZipUploadProgress, 
        setZipUploadStatus
      );

      if (!zipId) {
        setIsUploading(false);
        return;
      }

      // Create submission with upload IDs
      const formData = new FormData();
      formData.append('name', name);
      formData.append('task_name', taskName);
      formData.append('pdf_upload_id', pdfId);
      formData.append('zip_upload_id', zipId);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to create a submission');
        navigate('/login');
        setIsUploading(false);
        return;
      }

      const response = await fetch(API_ENDPOINTS.SUBMISSIONS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Your session has expired. Please log in again.');
          localStorage.removeItem('token');
          navigate('/login');
          setIsUploading(false);
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create submission');
      }

      setSuccess('Submission created successfully');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error creating submission:', error);
      setError(error.message || 'Failed to create submission. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (file, fileType) => {
    if (fileType === 'pdf') {
      setPdfFile(file);
      setPdfUploadStatus('idle');
      setPdfUploadProgress(0);
    } else {
      setZipFile(file);
      setZipUploadStatus('idle');
      setZipUploadProgress(0);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'uploading': return 'primary';
      case 'failed': return 'error';
      case 'paused': return 'warning';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'uploading': return 'Uploading...';
      case 'failed': return 'Failed';
      case 'paused': return 'Paused';
      default: return 'Ready';
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Create New Submission
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {error}
            </Typography>
            {(error.includes('session has expired') || error.includes('must be logged in')) && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => navigate('/login')}
                sx={{ mt: 1 }}
              >
                Go to Login
              </Button>
            )}
          </Box>
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Submission Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
            name="name"
            disabled={isUploading}
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Task</InputLabel>
            <Select
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              label="Task"
              name="task_name"
              disabled={isUploading}
            >
              {tasks.map((task) => (
                <MenuItem key={task} value={task}>
                  {task}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Year"
            value={currentYear}
            margin="normal"
            disabled
          />

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Extended Abstract (PDF, max 20MB)
                </Typography>
                <input
                  accept=".pdf"
                  type="file"
                  onChange={(e) => handleFileChange(e.target.files[0], 'pdf')}
                  required
                  name="pdf_file"
                  disabled={isUploading}
                />
                {pdfFile && (
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={getStatusText(pdfUploadStatus)}
                      color={getStatusColor(pdfUploadStatus)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {pdfUploadStatus === 'uploading' && (
                      <LinearProgress 
                        variant="determinate" 
                        value={pdfUploadProgress} 
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Submission Files (ZIP, max 20GB)
                </Typography>
                <input
                  accept=".zip"
                  type="file"
                  onChange={(e) => handleFileChange(e.target.files[0], 'zip')}
                  required
                  name="zip_file"
                  disabled={isUploading}
                />
                {zipFile && (
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={getStatusText(zipUploadStatus)}
                      color={getStatusColor(zipUploadStatus)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {zipUploadStatus === 'uploading' && (
                      <LinearProgress 
                        variant="determinate" 
                        value={zipUploadProgress} 
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isUploading || !name || !taskName || !pdfFile || !zipFile}
            sx={{ mt: 3 }}
          >
            {isUploading ? 'Creating Submission...' : 'Create Submission'}
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default NewSubmission; 